import { Fragment, type FC, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface DeleteOrderItemModalProps {
    isOpen: boolean;
    item: { id: string; product_name: string } | null;
    initialReason?: string; // 👈 NEW
    onClose: () => void;
    onConfirm: (reason: string) => Promise<void>;
    isSubmitting: boolean;
}

const labelClass = 'block text-sm font-medium text-gray-800';
const fieldClass =
    'block w-full rounded-lg border-0 ring-1 ring-inset ring-stone-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-stone-400 focus:ring-2 focus:ring-red-600 focus:outline-none';

const DeleteOrderItemModal: FC<DeleteOrderItemModalProps> = ({
                                                                 isOpen,
                                                                 item,
                                                                 initialReason = '', // 👈 NEW
                                                                 onClose,
                                                                 onConfirm,
                                                                 isSubmitting,
                                                             }) => {
    const [reason, setReason] = useState<string>('');
    const [error, setError] = useState<string>('');

    // Initialize reason when modal opens or initialReason changes
    useEffect(() => {
        if (isOpen) {
            setReason(initialReason);
        } else {
            setReason('');
            setError('');
        }
    }, [isOpen, initialReason]);

    const handleConfirm = async () => {
        setError('');

        if (!reason.trim()) {
            setError('Please provide a reason for deletion');
            return;
        }

        try {
            await onConfirm(reason.trim());
            setReason('');
            setError('');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete item');
        }
    };

    return (
        <Transition.Root show={isOpen && !!item} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-[1px]" />
                </Transition.Child>

                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative w-full transform overflow-hidden rounded-2xl bg-white ring-1 ring-stone-200 shadow-xl transition-all sm:max-w-md">
                                <div className="absolute right-0 top-0 pr-4 pt-4">
                                    <button
                                        type="button"
                                        className="rounded-md text-stone-400 hover:text-stone-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                                        onClick={onClose}
                                        disabled={isSubmitting}
                                        aria-label="Close"
                                    >
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>

                                <div className="px-5 py-6 sm:px-6">
                                    <div className="flex items-start gap-3">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 flex-shrink-0">
                                            <XMarkIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                                        </div>
                                        <div>
                                            <Dialog.Title as="h3" className="font-display text-xl tracking-tight text-gray-900">
                                                Delete order item
                                            </Dialog.Title>
                                            <p className="mt-1 text-sm text-gray-600">
                                                This action will permanently mark <span className="font-semibold">{item?.product_name}</span> as deleted.
                                            </p>
                                        </div>
                                    </div>

                                    <form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            void handleConfirm();
                                        }}
                                        className="mt-6 space-y-5"
                                    >
                                        <div>
                                            <label htmlFor="delete_reason" className={labelClass}>
                                                Reason for deletion <span className="text-red-600">*</span>
                                            </label>
                                            <textarea
                                                id="delete_reason"
                                                value={reason}
                                                onChange={(e) => setReason(e.target.value)}
                                                placeholder="e.g., Item out of stock, customer cancellation, damaged goods…"
                                                className={`${fieldClass} resize-none`}
                                                rows={4}
                                                disabled={isSubmitting}
                                                autoFocus
                                            />
                                        </div>

                                        <div className="rounded-lg bg-red-50 ring-1 ring-inset ring-red-200 p-3">
                                            <p className="text-xs font-semibold text-red-900 uppercase tracking-wide">Warning</p>
                                            <p className="mt-1 text-sm text-red-800">
                                                This deletion will be logged to the audit trail with your reason. Consider if you should adjust the quantity instead.
                                            </p>
                                        </div>

                                        {error && (
                                            <div className="rounded-lg bg-red-50 ring-1 ring-inset ring-red-200 p-3">
                                                <p className="text-sm text-red-900">{error}</p>
                                            </div>
                                        )}

                                        <div className="pt-2 flex items-center justify-end gap-3">
                                            <button
                                                type="button"
                                                className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-stone-300 hover:bg-stone-50 disabled:opacity-50"
                                                onClick={onClose}
                                                disabled={isSubmitting}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={isSubmitting || !reason.trim()}
                                                className="inline-flex items-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:opacity-70 disabled:cursor-not-allowed"
                                            >
                                                {isSubmitting ? 'Deleting…' : 'Delete item'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default DeleteOrderItemModal;